/**
 * FORGE API Client
 *
 * Centralized API service for all FORGE-related endpoints.
 * Uses axios for HTTP requests with proper error handling.
 */

import axios, { AxiosError } from 'axios';
import type {
  QuickModeRequest,
  QuickModeResponse,
  DetailedModeRequest,
  DetailedModeResponse,
  TriggerExecutionRequest,
  TriggerExecutionResponse,
  GetRequirementResponse,
  ListRequirementsResponse,
  RequirementListResponse,
  DashboardFilters,
  ReviewActionPayload,
  RequirementStatistics,
  ServiceInfo,
} from '../types/forge';

// Base URL for FORGE API — lazy evaluation so ForgeProvider can set it before first call.
// ForgeProvider sets __FORGE_API_URL__ to the full base path (e.g. '/api/v1/forge').
// Default to '/api/v1/forge' for relative URLs through the Vite proxy.
const forgeBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    if ((window as any).__FORGE_API_URL__) return (window as any).__FORGE_API_URL__;
    try {
      // @ts-ignore - Vite injects this at build time
      if (import.meta.env?.VITE_FORGE_API_URL) {
        // @ts-ignore
        return import.meta.env.VITE_FORGE_API_URL;
      }
    } catch {
      // Ignore if import.meta is not available
    }
  }
  return '/api/v1/forge';
};

/**
 * API Error class for better error handling
 */
export class ForgeApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errorCode?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ForgeApiError';
  }
}

/**
 * Handle API errors consistently
 */
const handleApiError = (error: AxiosError): never => {
  if (error.response) {
    // Server responded with error status
    const data = error.response.data as any;
    throw new ForgeApiError(
      data?.detail || data?.message || 'An error occurred',
      error.response.status,
      data?.error_code,
      data
    );
  } else if (error.request) {
    // Request made but no response
    throw new ForgeApiError('No response from server. Please check your connection.', 0);
  } else {
    // Error setting up request
    throw new ForgeApiError(error.message || 'Failed to make request');
  }
};

/**
 * Get authentication token from localStorage or context
 */
const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  // Try to get token from localStorage (common pattern)
  return localStorage.getItem('auth_token') || localStorage.getItem('access_token');
};

/**
 * Create axios instance with auth headers
 */
const createAuthenticatedAxios = () => {
  const token = getAuthToken();
  return axios.create({
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

/**
 * Quick Mode APIs — maps to POST /api/v1/forge/requirements/
 */
export const quickModeApi = {
  async submit(request: QuickModeRequest): Promise<QuickModeResponse> {
    try {
      const axiosInstance = createAuthenticatedAxios();
      const response = await axiosInstance.post(
        `${forgeBaseUrl()}/requirements/`,
        {
          title: request.message.slice(0, 200),
          description: request.message,
          requirement_type: 'new_feature',
          priority: 'medium',
          spec_metadata: { pageContext: request.pageContext },
        }
      );
      return {
        requirement: mapRequirementResponse(response.data),
        tier: 'standard',
        suggestDetailedMode: true,
      };
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

/**
 * Detailed Mode APIs — maps to POST /api/v1/forge/requirements/
 */
export const detailedModeApi = {
  async submit(request: DetailedModeRequest): Promise<DetailedModeResponse> {
    try {
      const axiosInstance = createAuthenticatedAxios();
      const response = await axiosInstance.post(
        `${forgeBaseUrl()}/requirements/`,
        {
          title: request.title,
          description: request.description,
          requirement_type: request.requirementType,
          priority: request.questionnaireData?.priority || 'medium',
          spec_metadata: {
            ...request.questionnaireData,
            pageContext: request.pageContext,
          },
        }
      );
      return { requirement: mapRequirementResponse(response.data) };
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

/**
 * Map backend RequirementResponse to frontend ForgeRequirement
 */
const mapRequirementResponse = (data: any): any => ({
  ...data,
  requirementType: data.requirement_type,
  requirement_type: data.requirement_type,
  tier: 'standard',
  status: data.status,
  serviceId: data.service_id || '',
  service_name: data.service_name || 'platform',
  createdBy: data.submitter_id || '',
  createdAt: data.created_at,
  created_at: data.created_at,
  updatedAt: data.updated_at,
  submitted_at: data.submitted_at,
  submitter_id: data.submitter_id,
  days_pending: data.days_pending ?? (data.created_at ? Math.floor((Date.now() - new Date(data.created_at).getTime()) / 86400000) : undefined),
});

/**
 * Execution APIs
 */
export const executionApi = {
  /**
   * Trigger execution for a requirement
   */
  async trigger(request: TriggerExecutionRequest): Promise<TriggerExecutionResponse> {
    try {
      const axiosInstance = createAuthenticatedAxios();
      const response = await axiosInstance.post<TriggerExecutionResponse>(
        `${forgeBaseUrl()}/execution/${request.requirementId}/trigger`,
        {}
      );
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  /**
   * Get SSE stream URL for execution progress
   */
  getStreamUrl(requirementId: number): string {
    const token = getAuthToken();
    const base = `${forgeBaseUrl()}/execution/${requirementId}/stream`;
    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
  },
};

/**
 * Requirement APIs
 */
export const requirementApi = {
  /**
   * Get requirement by ID
   */
  async getRequirement(id: number): Promise<GetRequirementResponse> {
    try {
      const axiosInstance = createAuthenticatedAxios();
      const response = await axiosInstance.get<GetRequirementResponse>(
        `${forgeBaseUrl()}/requirements/${id}`
      );
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  /**
   * List all requirements — GET /api/v1/forge/requirements/
   */
  async listRequirements(filters?: DashboardFilters & { page?: number; page_size?: number; sort_by?: string; sort_order?: string }): Promise<RequirementListResponse> {
    try {
      const axiosInstance = createAuthenticatedAxios();
      const params = new URLSearchParams();

      if (filters) {
        if (filters.status) params.append('status', filters.status);
        if (filters.requirement_type) params.append('type', filters.requirement_type);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.page_size) params.append('page_size', filters.page_size.toString());
      }

      const response = await axiosInstance.get(
        `${forgeBaseUrl()}/requirements/?${params.toString()}`
      );
      const data = response.data;
      const items = Array.isArray(data) ? data : data.items || data.requirements || [];
      const total = data.total ?? items.length;
      return { requirements: items.map(mapRequirementResponse), total };
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  /**
   * List all requirements (no status filter) — GET /api/v1/forge/requirements/
   */
  async listAll(filters?: DashboardFilters & { page?: number; page_size?: number }): Promise<RequirementListResponse> {
    return this.listRequirements(filters);
  },

  /**
   * List pending requirements (submitted + under_review) — GET /api/v1/forge/requirements/?status=submitted
   */
  async listPending(filters?: DashboardFilters & { page?: number; page_size?: number }): Promise<RequirementListResponse> {
    return this.listRequirements({ ...filters, status: 'submitted' });
  },

  /**
   * List approved requirements — GET /api/v1/forge/requirements/?status=approved
   */
  async listApproved(filters?: DashboardFilters & { page?: number; page_size?: number; sort_by?: string; sort_order?: string }): Promise<RequirementListResponse> {
    return this.listRequirements({ ...filters, status: 'approved' });
  },
};

/**
 * Admin APIs
 */
export const adminApi = {
  /**
   * Review a requirement — POST /api/v1/forge/requirements/{id}/approve or /reject
   */
  async reviewRequirement(
    requirementId: number,
    action: ReviewActionPayload
  ): Promise<{ success: boolean; message: string }> {
    try {
      const axiosInstance = createAuthenticatedAxios();
      const endpointMap: Record<string, string> = {
        'rejected': 'reject',
        'approved': 'approve',
        'deferred': 'defer',
        'needs_clarification': 'needs-clarification',
      };
      const endpoint = endpointMap[action.action] || 'approve';
      const response = await axiosInstance.post(
        `${forgeBaseUrl()}/requirements/${requirementId}/${endpoint}`,
        {
          reviewer_id: action.assigned_developer_id || 1,
          review_comments: action.reviewer_comments,
        }
      );
      return { success: true, message: response.data?.message || 'Review submitted' };
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  /**
   * Get requirement statistics — GET /api/v1/forge/admin/stats
   */
  async getStatistics(): Promise<RequirementStatistics> {
    try {
      const axiosInstance = createAuthenticatedAxios();
      const response = await axiosInstance.get<RequirementStatistics>(
        `${forgeBaseUrl()}/admin/stats`
      );
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

/**
 * Service APIs
 */
export const serviceApi = {
  /**
   * Get all available services for FORGE (no dedicated endpoint — returns empty list)
   */
  async getServices(): Promise<{ services: ServiceInfo[] }> {
    return { services: [] };
  },

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await axios.get(`${forgeBaseUrl()}/health`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

/**
 * Combined API object for easy imports
 */
export const forgeApi = {
  quickMode: quickModeApi,
  detailedMode: detailedModeApi,
  execution: executionApi,
  requirements: requirementApi,
  admin: adminApi,
  services: serviceApi,
};

export default forgeApi;
